import type { P2PEnvelope, P2PMessage, WebRTCSignalPayload } from '../protocol';
import { parseP2PEnvelopeFrame } from '../validation';
import type {
  DiagnosableTransportEndpoint,
  TransportDiagnostics,
  TransportDiagnosticsHandler,
  TransportMessageHandler,
  TransportStatusHandler,
  TransportUnsubscribe,
  TransportWaitingFor,
} from './types';

const SIGNAL_KIND = 'hanamikoji-webrtc-signal' as const;
const SDP_PREFIX = 'hanamikoji-broadcast-test:';

type WireMessage =
  | { kind: 'hello' | 'ack' | 'closed'; fromPeerId: string }
  | { kind: 'frame'; fromPeerId: string; frame: string };

/**
 * Browser-only deterministic transport used by Playwright. It still exercises
 * the real invite/answer and React UI paths, but avoids CI WebRTC flakiness.
 */
export class BroadcastChannelTestEndpoint implements DiagnosableTransportEndpoint {
  private readonly messageHandlers = new Set<TransportMessageHandler>();
  private readonly disconnectHandlers = new Set<TransportStatusHandler>();
  private readonly reconnectHandlers = new Set<TransportStatusHandler>();
  private readonly diagnosticsHandlers = new Set<TransportDiagnosticsHandler>();
  private readonly pendingMessages: P2PMessage[] = [];
  private readonly channel: BroadcastChannel;
  private online = false;
  private closed = false;
  private answerApplied: boolean;
  private remoteSeen = false;
  private waitingFor: TransportWaitingFor;
  private helloTimer: ReturnType<typeof globalThis.setInterval> | null = null;
  private lastRemoteSeenAt = 0;
  private readonly handlePageHide = () => this.disconnect();

  private constructor(
    readonly peerId: string,
    private readonly remotePeerId: string,
    private readonly channelId: string,
    role: 'host' | 'player',
  ) {
    this.answerApplied = role === 'player';
    this.waitingFor = role === 'host' ? 'answer' : 'connection';
    this.channel = new BroadcastChannel(`hanamikoji-e2e-${channelId}`);
    this.channel.onmessage = event => this.receive(event.data as WireMessage);
    globalThis.addEventListener?.('pagehide', this.handlePageHide);
    this.startHello();
  }

  static async createOffer(options: { hostPeerId: string; remotePeerId: string; roomId: string }): Promise<{ endpoint: BroadcastChannelTestEndpoint; signal: WebRTCSignalPayload }> {
    const channelId = createChannelId();
    return {
      endpoint: new BroadcastChannelTestEndpoint(options.hostPeerId, options.remotePeerId, channelId, 'host'),
      signal: buildSignal('host-offer', options.roomId, options.hostPeerId, options.remotePeerId, channelId),
    };
  }

  static async acceptOffer(options: { offer: WebRTCSignalPayload }): Promise<{ endpoint: BroadcastChannelTestEndpoint; signal: WebRTCSignalPayload }> {
    const offer = options.offer;
    if (offer.role !== 'host-offer' || offer.description.type !== 'offer') throw new Error('信令格式无效：请粘贴 Host offer。');
    const channelId = readChannelId(offer);
    return {
      endpoint: new BroadcastChannelTestEndpoint(offer.remotePeerId, offer.hostPeerId, channelId, 'player'),
      signal: buildSignal('player-answer', offer.roomId, offer.hostPeerId, offer.remotePeerId, channelId),
    };
  }

  async applyAnswer(answer: WebRTCSignalPayload): Promise<void> {
    if (answer.role !== 'player-answer' || answer.description.type !== 'answer') throw new Error('信令格式无效：请粘贴 Player answer。');
    if (answer.hostPeerId !== this.peerId || answer.remotePeerId !== this.remotePeerId || readChannelId(answer) !== this.channelId) {
      throw new Error('信令不匹配：answer 不属于当前离线房间。');
    }
    this.answerApplied = true;
    this.waitingFor = 'connection';
    this.emitDiagnostics();
    this.announce('hello');
    this.maybeConnect();
  }

  get isOnline(): boolean {
    return this.online && !this.closed;
  }

  getDiagnostics(): TransportDiagnostics {
    return {
      transport: 'broadcast-channel-test',
      connectionState: this.closed ? 'closed' : this.online ? 'connected' : 'connecting',
      iceConnectionState: this.closed ? 'closed' : this.online ? 'connected' : 'checking',
      signalingState: this.answerApplied ? 'stable' : 'have-local-offer',
      dataChannelState: this.closed ? 'closed' : this.online ? 'open' : 'connecting',
      waitingFor: this.waitingFor,
      updatedAt: Date.now(),
    };
  }

  onDiagnostics(handler: TransportDiagnosticsHandler): TransportUnsubscribe {
    this.diagnosticsHandlers.add(handler);
    handler(this.getDiagnostics());
    return () => this.diagnosticsHandlers.delete(handler);
  }

  send(toPeerId: string, message: P2PMessage): void {
    if (toPeerId !== this.remotePeerId) throw new Error(`No test transport route to ${toPeerId}`);
    if (!this.isOnline) {
      this.pendingMessages.push(message);
      return;
    }
    const frame = JSON.stringify({ fromPeerId: this.peerId, toPeerId, message } satisfies P2PEnvelope);
    this.channel.postMessage({ kind: 'frame', fromPeerId: this.peerId, frame } satisfies WireMessage);
  }

  onMessage(handler: TransportMessageHandler): TransportUnsubscribe {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  onDisconnect(handler: TransportStatusHandler): TransportUnsubscribe {
    this.disconnectHandlers.add(handler);
    return () => this.disconnectHandlers.delete(handler);
  }

  onReconnect(handler: TransportStatusHandler): TransportUnsubscribe {
    this.reconnectHandlers.add(handler);
    return () => this.reconnectHandlers.delete(handler);
  }

  disconnect(): void {
    if (this.closed) return;
    this.announce('closed');
    this.closed = true;
    this.stopHello();
    globalThis.removeEventListener?.('pagehide', this.handlePageHide);
    this.channel.close();
    this.markDisconnected(true);
  }

  reconnect(): void {
    // Recovery intentionally creates a new invite and a new endpoint.
  }

  private receive(message: WireMessage): void {
    if (!message || message.fromPeerId !== this.remotePeerId || this.closed) return;
    this.lastRemoteSeenAt = Date.now();
    if (message.kind === 'frame') {
      const envelope = parseP2PEnvelopeFrame(message.frame, {
        expectedFromPeerId: this.remotePeerId,
        expectedToPeerId: this.peerId,
      });
      if (!envelope) return;
      for (const handler of [...this.messageHandlers]) handler(envelope);
      return;
    }
    if (message.kind === 'closed') {
      this.markDisconnected();
      return;
    }
    this.remoteSeen = true;
    if (message.kind === 'hello') this.announce('ack');
    this.maybeConnect();
  }

  private maybeConnect(): void {
    if (!this.answerApplied || !this.remoteSeen || this.closed) return;
    const wasOnline = this.online;
    this.online = true;
    this.waitingFor = null;
    this.emitDiagnostics();
    this.flushPending();
    if (!wasOnline) for (const handler of [...this.reconnectHandlers]) handler(this.remotePeerId);
  }

  private markDisconnected(force = false): void {
    if (!this.online && !force) return;
    this.online = false;
    if (!this.closed) this.waitingFor = 'connection';
    this.emitDiagnostics();
    for (const handler of [...this.disconnectHandlers]) handler(this.remotePeerId);
  }

  private flushPending(): void {
    while (this.isOnline && this.pendingMessages.length > 0) {
      this.send(this.remotePeerId, this.pendingMessages.shift()!);
    }
  }

  private startHello(): void {
    this.announce('hello');
    this.helloTimer = globalThis.setInterval(() => {
      this.announce('hello');
      if (this.online && this.lastRemoteSeenAt > 0 && Date.now() - this.lastRemoteSeenAt > 750) {
        this.markDisconnected();
      }
    }, 100);
  }

  private stopHello(): void {
    if (!this.helloTimer) return;
    globalThis.clearInterval(this.helloTimer);
    this.helloTimer = null;
  }

  private announce(kind: 'hello' | 'ack' | 'closed'): void {
    if (this.closed) return;
    this.channel.postMessage({ kind, fromPeerId: this.peerId } satisfies WireMessage);
  }

  private emitDiagnostics(): void {
    const diagnostics = this.getDiagnostics();
    for (const handler of [...this.diagnosticsHandlers]) handler(diagnostics);
  }
}

function buildSignal(
  role: 'host-offer' | 'player-answer',
  roomId: string,
  hostPeerId: string,
  remotePeerId: string,
  channelId: string,
): WebRTCSignalPayload {
  return {
    kind: SIGNAL_KIND,
    version: 1,
    role,
    roomId,
    hostPeerId,
    remotePeerId,
    description: { type: role === 'host-offer' ? 'offer' : 'answer', sdp: `${SDP_PREFIX}${channelId}` },
    createdAt: Date.now(),
  };
}

function readChannelId(signal: WebRTCSignalPayload): string {
  const sdp = signal.description.sdp ?? '';
  if (!sdp.startsWith(SDP_PREFIX)) throw new Error('测试 transport 信令无效。');
  return sdp.slice(SDP_PREFIX.length);
}

function createChannelId(): string {
  const random = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;
  return random.replace(/[^A-Za-z0-9_-]/g, '');
}
